import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ArrowLeft, Search, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { searchInstagramUsersPublic, InstagramSearchResult } from 'services/instagram/instagramSearchService';
import { addInstagramAccount } from 'services/auth/instagramAuthService';
import { useInstagramConnection } from 'contexts/InstagramConnectionContext';
import VerificationCodeModal from 'components/modals/VerificationCodeModal';
import { useTheme } from 'contexts/ThemeContext';
import { getErrorMessage } from 'utils/errors';

const connectingAnimationStyles = `
@keyframes overlayShimmer {
  0% { left: -40%; }
  100% { left: 100%; }
}
`;

// Country list for location detection
const countryList = [
  { code: "AF", name: "Afghanistan", callingCode: "+93" },
  { code: "AL", name: "Albania", callingCode: "+355" },
  { code: "DZ", name: "Algeria", callingCode: "+213" },
  { code: "AR", name: "Argentina", callingCode: "+54" },
  { code: "AU", name: "Australia", callingCode: "+61" },
  { code: "AT", name: "Austria", callingCode: "+43" },
  { code: "BD", name: "Bangladesh", callingCode: "+880" },
  { code: "BE", name: "Belgium", callingCode: "+32" },
  { code: "BR", name: "Brazil", callingCode: "+55" },
  { code: "CA", name: "Canada", callingCode: "+1" },
  { code: "CN", name: "China", callingCode: "+86" },
  { code: "CO", name: "Colombia", callingCode: "+57" },
  { code: "DE", name: "Germany", callingCode: "+49" },
  { code: "EG", name: "Egypt", callingCode: "+20" },
  { code: "ES", name: "Spain", callingCode: "+34" },
  { code: "FR", name: "France", callingCode: "+33" },
  { code: "GB", name: "United Kingdom", callingCode: "+44" },
  { code: "GH", name: "Ghana", callingCode: "+233" },
  { code: "IN", name: "India", callingCode: "+91" },
  { code: "ID", name: "Indonesia", callingCode: "+62" },
  { code: "IT", name: "Italy", callingCode: "+39" },
  { code: "JP", name: "Japan", callingCode: "+81" },
  { code: "KE", name: "Kenya", callingCode: "+254" },
  { code: "KR", name: "South Korea", callingCode: "+82" },
  { code: "MX", name: "Mexico", callingCode: "+52" },
  { code: "MY", name: "Malaysia", callingCode: "+60" },
  { code: "NG", name: "Nigeria", callingCode: "+234" },
  { code: "NL", name: "Netherlands", callingCode: "+31" },
  { code: "PH", name: "Philippines", callingCode: "+63" },
  { code: "PK", name: "Pakistan", callingCode: "+92" },
  { code: "PL", name: "Poland", callingCode: "+48" },
  { code: "PT", name: "Portugal", callingCode: "+351" },
  { code: "RU", name: "Russia", callingCode: "+7" },
  { code: "SA", name: "Saudi Arabia", callingCode: "+966" },
  { code: "SG", name: "Singapore", callingCode: "+65" },
  { code: "TH", name: "Thailand", callingCode: "+66" },
  { code: "TR", name: "Turkey", callingCode: "+90" },
  { code: "AE", name: "United Arab Emirates", callingCode: "+971" },
  { code: "US", name: "United States", callingCode: "+1" },
  { code: "VN", name: "Vietnam", callingCode: "+84" },
  { code: "ZA", name: "South Africa", callingCode: "+27" },
];

type OverlayStep = 'search' | 'password' | 'connecting';

interface AccountData {
  _id: string;
  username: string;
  name?: string;
  profile_picture_base64?: string;
}

interface AddAccountOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  reauthUsername?: string;
  reauthProfilePic?: string;
  accountData?: AccountData | null;
  allAccounts?: AccountData[];
}

const AddAccountOverlay: React.FC<AddAccountOverlayProps> = ({ isOpen, onClose, reauthUsername, reauthProfilePic, accountData, allAccounts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startConnection, connectionState, submitMethod, resetConnection } = useInstagramConnection();

  // Step state
  const [step, setStep] = useState<OverlayStep>('search');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<InstagramSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Selected user state
  const [selectedUser, setSelectedUser] = useState<InstagramSearchResult | null>(null);

  // Password state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA local state (for method selection shown in overlay)
  const [selectedMethod, setSelectedMethod] = useState('');

  // Verification code modal state
  const [showCodeModal, setShowCodeModal] = useState(false);

  // Location data
  const [locationData, setLocationData] = useState<{
    country: string | null;
    countryCode: string | null;
    locale: string | null;
    timezoneOffset: number | null;
  }>({ country: null, countryCode: null, locale: null, timezoneOffset: null });

  // Dark mode
  const { isDark } = useTheme();

  // Reset state when overlay opens
  useEffect(() => {
    if (isOpen) {
      // If a connection is already in progress, go directly to connecting step
      if (connectionState.step === 'connecting' || connectionState.step === 'code' || connectionState.step === 'method') {
        setStep('connecting');
      } else if (reauthUsername) {
        setSelectedUser({ username: reauthUsername, full_name: '', profile_pic_url: reauthProfilePic || '', id: 0, is_private: false, is_verified: false });
        setStep('password');
      } else {
        setStep('search');
        setSelectedUser(null);
      }
      setSearchQuery('');
      setSearchResults([]);
      setHasSearched(false);
      setPassword('');
      setLoading(false);
      setSelectedMethod('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, reauthUsername]);

  // Auto-close overlay and dismiss banner on successful connection
  useEffect(() => {
    if (connectionState.step === 'success') {
      // Small delay so the user can briefly see "Authentication successful" status
      const timer = setTimeout(() => {
        resetConnection();
        if (isOpen) {
          onClose();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [connectionState.step, isOpen, onClose, resetConnection]);

  // Fetch location data
  useEffect(() => {
    if (isOpen) {
      const rawLocale = navigator.language || 'en-US';
      let detectedCountry = 'US';
      let formattedLocale = rawLocale;

      if (rawLocale.includes('-')) {
        const parts = rawLocale.split('-');
        detectedCountry = parts[1]?.toUpperCase() || 'US';
        formattedLocale = rawLocale.replace('-', '_');
      } else if (rawLocale.includes('_')) {
        const parts = rawLocale.split('_');
        detectedCountry = parts[1]?.toUpperCase() || 'US';
      } else {
        formattedLocale = `${rawLocale.toLowerCase()}_${detectedCountry}`;
      }

      const timezoneOffset = new Date().getTimezoneOffset() * -1;
      const countryInfo = countryList.find(c => c.code === detectedCountry);
      const countryCode = countryInfo ? countryInfo.callingCode : '+1';

      setLocationData({ country: detectedCountry, countryCode, locale: formattedLocale, timezoneOffset });
    }
  }, [isOpen]);

  // Watch for connection state changes from context (2FA prompts)
  useEffect(() => {
    if (isOpen && step === 'connecting') {
      if (connectionState.step === 'error') {
        // Stay on connecting step — error banner will show with the message
        setLoading(false);
      }
    }
  }, [isOpen, step, connectionState.step]);

  // Search handler
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const results = await searchInstagramUsersPublic(searchQuery.trim());
      setSearchResults(results);
    } catch {
      toast.error(t('addAccount.searchFailed'));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Select user from results
  const handleSelectUser = (user: InstagramSearchResult) => {
    setSelectedUser(user);
    setStep('password');
    setSearchResults([]);
  };

  // Submit password — starts connection via global context
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !password) return;

    setLoading(true);
    setStep('connecting');

    // Start the global connection (WebSocket + timer)
    startConnection(selectedUser.username);

    try {
      const { country, countryCode, locale, timezoneOffset } = locationData;
      if (!country || !countryCode || !locale || timezoneOffset === null) {
        throw new Error('Location data not fully loaded');
      }
      await addInstagramAccount(selectedUser.username, password, country, countryCode, locale, timezoneOffset);
      // API call succeeded — the WebSocket will handle status updates
      // Stay on connecting screen until user clicks "Skip for now" or "Set up rules"
    } catch (error: unknown) {
      setLoading(false);
      setStep('password');
      toast.error(getErrorMessage(error, 'Login failed. Please try again.'));
    }
  };

  // Submit 2FA method via context
  const handleMethodSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMethod) return;
    setLoading(true);
    await submitMethod(selectedMethod);
    setLoading(false);
    // After method submission, close overlay — banner will persist
    setTimeout(() => { onClose(); }, 1000);
  };

  if (!isOpen) return null;

  const overlayBg = isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)';
  const textPrimary = isDark ? 'text-[#E2E8F0]' : 'text-[#314158]';
  const textSecondary = isDark ? 'text-[#E2E8F0]' : 'text-[#314158]';
  const textMuted = isDark ? 'text-[#90A1B9]' : 'text-[#62748E]';
  const cardBg = isDark ? 'bg-[#0F172B]' : 'bg-white';
  const borderColor = isDark ? 'border-[#1D293D]' : 'border-[#E2E8F0]';
  const searchShadow = isDark
    ? '0px 4px 6px -1px rgba(2, 6, 24, 0.5), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'
    : '0px 4px 6px -1px rgba(43, 127, 255, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)';
  const usernameColor = isDark ? 'text-[#51A2FF]' : 'text-[#2B7FFF]';

  // Determine what to show based on overlay step + connection context step
  const showMethod = step === 'connecting' && connectionState.step === 'method';
  const isVerificationRequired = step === 'connecting' && connectionState.step === 'code';
  const isError = step === 'connecting' && connectionState.step === 'error';
  const showConnecting = step === 'connecting' && !showMethod;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{ background: overlayBg, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      >
        {/* Top-left button: Skip for now / Back (hidden on connecting step) */}
        {!showConnecting && (
          <div className="p-4 sm:p-6">
            {step === 'password' && !reauthUsername ? (
              <button
                onClick={() => { setStep('search'); setPassword(''); }}
                className={`flex items-center hover:opacity-80 transition-opacity rounded-lg ${isDark ? 'text-[#E2E8F0]' : 'text-[#314158]'}`}
                style={{ padding: '8px 12px', gap: '4px', background: 'rgba(255, 255, 255, 0.01)', fontFamily: 'Geist, sans-serif', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
              >
                <ArrowLeft size={20} className={isDark ? 'text-[#CAD5E2]' : 'text-[#45556C]'} />
                {t('addAccount.back') || 'Back'}
              </button>
            ) : (
              <button
                onClick={onClose}
                className={`flex items-center hover:opacity-80 transition-opacity rounded-lg ${isDark ? 'text-[#E2E8F0]' : 'text-[#314158]'}`}
                style={{ padding: '8px 12px', gap: '4px', background: 'rgba(255, 255, 255, 0.01)', fontFamily: 'Geist, sans-serif', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}
              >
                <X size={20} className={isDark ? 'text-[#CAD5E2]' : 'text-[#45556C]'} />
                {t('addAccount.skipForNow') || 'Skip for now'}
              </button>
            )}
          </div>
        )}

        {/* Banner at top — absolutely positioned on connecting step */}
        {showConnecting && (
          <div
            className="absolute left-2 right-2 top-2 z-[3]"
            style={{
              padding: '2px',
              borderRadius: '12px',
              background: isError
                ? 'linear-gradient(90deg, #FF2056 0%, #FF6B8A 10%, rgba(255, 255, 255, 0) 25%)'
                : 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 10%, rgba(255, 255, 255, 0) 25%)',
            }}
          >
            <div
              className={`w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 ${cardBg}`}
              style={{
                padding: '12px 16px',
                boxShadow: isDark
                  ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  : '0px 10px 15px -3px rgba(43, 127, 255, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="flex flex-col justify-center items-start">
                <p
                  className={`${textPrimary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '20px' }}
                >
                  {isError
                    ? (t('addAccount.connectionIssue') || 'Connection issue')
                    : isVerificationRequired
                      ? (t('addAccount.verificationRequired') || 'Verification required')
                      : (t('addAccount.connectingBanner') || 'Connecting Instagram...')}
                </p>
                <p
                  className={`${textMuted}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}
                >
                  {isError
                    ? (connectionState.errorMessage || t('addAccount.connectionIssueDescription') || "We couldn't connect to your Instagram account right now.")
                    : isVerificationRequired
                      ? (t('addAccount.verificationBannerSub') || 'Instagram sent a verification code to your email address.')
                      : (connectionState.status || t('addAccount.connectingBannerSub') || 'This usually takes around 5 minutes.')}
                </p>
              </div>

              {/* Error: Try to reconnect button */}
              {isError && (
                <button
                  onClick={() => {
                    resetConnection();
                    setStep('password');
                    setPassword('');
                    setLoading(false);
                  }}
                  className="flex-shrink-0 text-white transition-opacity hover:opacity-90"
                  style={{
                    background: '#FF2056',
                    borderRadius: '8px',
                    height: '36px',
                    padding: '8px 12px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Geist, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  {t('addAccount.tryToReconnect') || 'Try to reconnect'}
                </button>
              )}

              {/* Enter verification code button — only when code is requested */}
              {isVerificationRequired && (
                <button
                  onClick={() => setShowCodeModal(true)}
                  className="flex-shrink-0 text-white transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                    borderRadius: '8px',
                    height: '36px',
                    padding: '8px 16px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Geist, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  {t('addAccount.enterVerificationCode') || 'Enter verification code'}
                </button>
              )}

              {/* Shimmer progress bar at bottom of banner */}
              {!isError && !isVerificationRequired && (
                <>
                  <style>{connectingAnimationStyles}</style>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: isDark ? 'rgba(43, 127, 255, 0.15)' : 'rgba(43, 127, 255, 0.1)',
                      overflow: 'hidden',
                      borderRadius: '0 0 10px 10px',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        width: '40%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, #2B7FFF, #AD46FF, transparent)',
                        animation: 'overlayShimmer 1.8s ease-in-out infinite',
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6">
          {/* STEP: SEARCH */}
          {step === 'search' && (
            <div className="w-full max-w-[480px] flex flex-col items-center gap-4 sm:gap-6">
              <div className="text-center space-y-2 sm:space-y-4">
                <h1
                  className={`text-xl sm:text-2xl ${textPrimary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
                >
                  {t('addAccount.findTitle') || 'Find your Instagram account'}
                </h1>
                <p
                  className={`text-sm sm:text-base ${textSecondary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400 }}
                >
                  {t('addAccount.findDescription') || 'Search by username to select the account you want to connect.'}
                </p>
              </div>

              {/* Search bar */}
              <div className="w-full relative">
                <div
                  className="flex flex-row items-center"
                  style={{
                    padding: '8px',
                    gap: '4px',
                    background: isDark ? '#0F172B' : '#FFFFFF',
                    boxShadow: searchShadow,
                    borderRadius: '16px',
                  }}
                >
                  <div
                    className="flex flex-row items-start flex-1"
                    style={{
                      padding: '8px 12px',
                      gap: '4px',
                      background: isDark ? '#0F172B' : '#FFFFFF',
                      borderRadius: '8px',
                    }}
                  >
                    <Search size={20} className={`${isDark ? 'text-[#62748E]' : 'text-[#90A1B9]'} flex-shrink-0`} />
                    <input
                      type="text"
                      placeholder={t('addAccount.searchPlaceholder') || 'Search by username'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                      className={`flex-1 text-sm font-normal outline-none bg-transparent ${isDark ? 'text-[#E2E8F0] placeholder-[#62748E]' : 'text-[#314158] placeholder-[#90A1B9]'}`}
                      style={{ fontFamily: 'Geist, sans-serif', fontSize: '14px', lineHeight: '20px' }}
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={searching || !searchQuery.trim()}
                    className="flex items-center justify-center disabled:opacity-50 flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      padding: '8px',
                      background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                      borderRadius: '8px',
                    }}
                  >
                    {searching ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Search size={20} className="text-white" />
                    )}
                  </button>
                </div>

                {/* Search results dropdown */}
                {hasSearched && searchResults.length > 0 && (
                  <div
                    className={`mt-3 rounded-2xl ${cardBg} max-h-[320px] overflow-y-auto overflow-x-hidden p-2 scrollbar-hide flex flex-col gap-0.5`}
                    style={{ boxShadow: searchShadow }}
                  >
                    {searchResults.map((user, index) => (
                      <button
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors text-left ${
                          isDark
                            ? 'hover:bg-[#1D293D] hover:border-[#1D293D]'
                            : 'hover:bg-[#F8FAFC] hover:border-[#F1F5F9]'
                        } ${index === 0
                            ? isDark ? 'bg-[#1D293D] border border-[#1D293D]' : 'bg-[#F8FAFC] border border-[#F1F5F9]'
                            : 'border border-transparent'
                        }`}
                      >
                        <img
                          src={user.profile_pic_url}
                          alt={user.username}
                          className="w-8 h-8 rounded-xl object-cover flex-shrink-0 bg-gray-100"
                          onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="8" r="4"/><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/></svg>'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="truncate" style={{ fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '20px' }}><span className={usernameColor}>@</span><span className={`${textPrimary}`}>{user.username}</span></p>
                          <p className={`${textMuted} truncate`} style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}>{user.full_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {hasSearched && searchResults.length === 0 && !searching && (
                  <p className={`mt-4 text-center ${textMuted}`} style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}>
                    {t('addAccount.noAccountsFound')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP: PASSWORD */}
          {step === 'password' && selectedUser && (
            <div className="w-full max-w-[480px] flex flex-col items-center gap-4 sm:gap-6">
              {/* Selected user pill */}
              <div className={`flex items-center gap-1 px-3 py-2 rounded-lg border ${isDark ? 'border-[#314158]' : 'border-[#E2E8F0]'} ${cardBg}`}>
                {selectedUser.profile_pic_url && (
                  <img
                    src={selectedUser.profile_pic_url}
                    alt={selectedUser.username}
                    className="w-5 h-5 rounded-xl object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <span
                  className={`${isDark ? 'text-[#51A2FF]' : 'text-[#2B7FFF]'}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '20px' }}
                >@{selectedUser.username}</span>
              </div>

              <div className="text-center space-y-2 sm:space-y-4">
                <h1
                  className={`text-xl sm:text-2xl ${textPrimary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
                >
                  {t('addAccount.enterPassword') || 'Enter your Instagram password'}
                </h1>
                <p
                  className={`text-sm sm:text-base ${textSecondary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400 }}
                >
                  {t('addAccount.secureConnect') || 'To securely connect your account'}
                </p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="w-full space-y-3 sm:space-y-4">
                <div
                  className="flex flex-row items-center"
                  style={{
                    padding: '8px',
                    gap: '4px',
                    background: isDark ? '#0F172B' : '#FFFFFF',
                    boxShadow: searchShadow,
                    borderRadius: '16px',
                  }}
                >
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('addAccount.passwordPlaceholder') || 'Enter your password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className={`flex-1 outline-none bg-transparent ${isDark ? 'text-[#E2E8F0] placeholder-[#62748E]' : 'text-[#314158] placeholder-[#90A1B9]'}`}
                    style={{ padding: '8px 12px', fontFamily: 'Geist, sans-serif', fontSize: '14px', lineHeight: '20px', borderRadius: '8px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`flex items-center justify-center shrink-0 ${isDark ? 'text-[#62748E] hover:text-[#90A1B9]' : 'text-[#90A1B9] hover:text-[#62748E]'}`}
                    style={{ padding: '8px', borderRadius: '8px' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Trust badges */}
                <div className="w-full flex flex-col items-start" style={{ gap: '4px' }}>
                  <div
                    className={`flex flex-row items-center ${isDark ? 'text-[#90A1B9]' : 'text-[#62748E]'}`}
                    style={{ gap: '6px', fontFamily: 'Geist, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}
                  >
                    <span>🔒</span>
                    <span>{t('addAccount.credentialsNotStored') || 'Your credentials are never stored'}</span>
                  </div>
                  <div
                    className={`flex flex-row items-center ${isDark ? 'text-[#90A1B9]' : 'text-[#62748E]'}`}
                    style={{ gap: '6px', fontFamily: 'Geist, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}
                  >
                    <span>🕐</span>
                    <span>{t('addAccount.connectionTime') || 'Connection takes about 5 minutes'}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!password || loading}
                  className="w-full text-white disabled:opacity-50 transition-opacity"
                  style={{ height: '48px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('addAccount.connectingBanner') || 'Connecting...'}</span>
                    </div>
                  ) : (
                    t('addAccount.connectAccount') || 'Connect account'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* STEP: CONNECTING */}
          {showConnecting && (
            <div className="w-full max-w-[480px] flex flex-col justify-center items-center gap-4 sm:gap-6">
              {/* Title + Description */}
              <div className="w-full flex flex-col items-center gap-2 sm:gap-4">
                <h1
                  className={`text-xl sm:text-2xl text-center ${textPrimary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
                >
                  {t('addAccount.connecting') || "We're connecting your Instagram account"}
                </h1>
                <p
                  className={`text-sm sm:text-base text-center ${textSecondary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400 }}
                >
                  {t('addAccount.connectingDescription') || 'This may take a few minutes. You can start setting up your automations while we finish connecting your account.'}
                </p>
              </div>

              {/* Buttons */}
              <div className="w-full flex flex-col items-start gap-2">
                <button
                  onClick={() => { onClose(); navigate('/rules/new', { state: { selectedAccount: accountData || null, accounts: allAccounts || [] } }); }}
                  className="w-full text-white transition-opacity"
                  style={{
                    height: '48px',
                    background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontFamily: 'Geist, sans-serif',
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                  }}
                >
                  {t('addAccount.setupRules') || 'Set up rules'}
                </button>

                <button
                  onClick={onClose}
                  className={`w-full hover:opacity-80 transition-opacity ${textPrimary}`}
                  style={{
                    height: '48px',
                    background: 'rgba(255, 255, 255, 0.01)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontFamily: 'Geist, sans-serif',
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                  }}
                >
                  {t('addAccount.skipForNow') || 'Skip for now'}
                </button>
              </div>
            </div>
          )}

          {/* STEP: METHOD (2FA method selection) — shown in overlay if still open */}
          {showMethod && (
            <div className="w-full max-w-[480px] flex flex-col items-center gap-4 sm:gap-6">
              <div className="text-center space-y-2 sm:space-y-4">
                <h1
                  className={`text-xl sm:text-2xl ${textPrimary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
                >
                  {t('addAccount.selectVerificationMethod')}
                </h1>
                <p
                  className={`text-sm sm:text-base ${textSecondary}`}
                  style={{ fontFamily: 'Geist, sans-serif', fontWeight: 400 }}
                >{connectionState.status}</p>
              </div>

              <form onSubmit={handleMethodSubmit} className="w-full space-y-4">
                <div className="space-y-2">
                  {connectionState.methods.map((method, index: number) => (
                    <div
                      key={index}
                      onClick={() => !loading && setSelectedMethod(method.type)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedMethod === method.type
                          ? `border-blue-500 ${isDark ? 'bg-[#314158]' : 'bg-blue-50'}`
                          : `${borderColor} ${cardBg} hover:border-gray-300`
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedMethod === method.type ? 'border-blue-500' : 'border-gray-300'
                        }`}>
                          {selectedMethod === method.type && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${textPrimary}`}>{method.type}</p>
                          <p className={`text-xs ${textMuted}`}>{method.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={!selectedMethod || loading}
                  className="w-full text-white disabled:opacity-50 transition-opacity"
                  style={{ height: '48px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}
                >
                  {loading ? t('addAccount.submitting') : t('addAccount.continue')}
                </button>

                {connectionState.timerActive && (
                  <p className={`text-center text-xs ${textMuted}`}>
                    {t('addAccount.timeRemaining')}: {Math.floor(connectionState.timeLeft / 60).toString().padStart(2, '0')}:{(connectionState.timeLeft % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Verification code modal — opens on top of the overlay */}
      <VerificationCodeModal
        isOpen={showCodeModal}
        onClose={() => setShowCodeModal(false)}
      />
    </>,
    document.body
  );
};

export default AddAccountOverlay;
